import { useTranslation } from "react-i18next";

import CheckIcon from "assets/icons/check.svg?react";
import InfoIcon from "assets/icons/info-new.svg?react";

import { convertValueToLocale } from "helpers/convertValueToLocale";

import { getSupportsData } from "./ObjectDetails.helpers";

import { Popover } from "shared/ui/components/Popover";
import { Typography } from "shared/ui/components/Typography";

import styles from "./ObjectDetails.module.scss";

const ObjectDetails = ({
  activePosition,
  conclusionsPro,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
}) => {
  const { t } = useTranslation();

  const { supportData, actualRates, actualAmounts } = getSupportsData(
    conclusionsPro,
    activePosition
  );

  return (
    <div className={styles.supportDataWrapper}>
      <Typography
        variant="bodyM"
        weight="semiBold"
        className={styles.supportDataTitle}
      >
        {t("investment_page.financial_support")}
      </Typography>
      <div className={styles.supportData}>
        {supportData.isLinearAfa400Enabled && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.linear_afa_3")}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  Die lineare AfA von 3 % entspricht einer Abschreibungsdauer
                  von 400 Monaten (33,3 Jahren). Jährlich wird ein
                  gleichbleibender Betrag in Höhe von 3 % der Anschaffungs- oder
                  Herstellungskosten steuerlich abgesetzt.
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {!supportData.isLinearAfa400Enabled && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.linear_afa_2")}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  Die lineare AfA von 2 % entspricht einer Abschreibungsdauer
                  von 600 Monaten (50 Jahren). Jährlich wird ein
                  gleichbleibender Betrag in Höhe von 2 % der Anschaffungs- oder
                  Herstellungskosten steuerlich abgesetzt.
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {supportData.isSonderAfaEnabled && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.special_afa")}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.special_depreciation_tooltip_1"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.special_depreciation_tooltip_2"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.special_depreciation_tooltip_3"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.special_depreciation_tooltip_4"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.special_depreciation_tooltip_5"
                  )}
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {supportData.isDegressiveAfaEnabled && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.degressive_afa")}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.degressive_depreciation_tooltip_1"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.degressive_depreciation_tooltip_2"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.degressive_depreciation_tooltip_3"
                  )}
                </Typography>

                <Typography variant="bodyS" weight="medium">
                  {t(
                    "calculator_pro_page.analytics.degressive_depreciation_tooltip_4"
                  )}
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {supportData.isKfw297Limit150Enabled && actualRates && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.kfw_298", {
                amount: actualAmounts || convertValueToLocale(150000),
                rate: actualRates,
              })}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  Es handelt sich um ein KfW-Förderprogramm 298 für
                  energieeffiziente Neubauten oder Sanierungen mit einer
                  maximalen Kredithöhe von 150.000EUR mit günstigeren
                  Konditionen als marktüblich.
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {supportData.isKfw297Limit100Enabled && actualRates && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.kfw_298", {
                amount: actualAmounts || convertValueToLocale(100000),
                rate: actualRates,
              })}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  Es handelt sich um ein KfW-Förderprogramm 298 für
                  energieeffiziente Neubauten oder Sanierungen mit einer
                  maximalen Kredithöhe von 100.000EUR mit günstigeren
                  Konditionen als marktüblich.
                </Typography>
              </div>
            </Popover>
          </div>
        )}
        {conclusionsPro?.baseCalculation?.isKfw297Limit100Enabled ||
          (conclusionsPro?.baseCalculation?.isKfw297Limit150Enabled && (
            <div className={styles.supportDataItem}>
              <CheckIcon className={styles.checkIcon} />
              <Typography
                variant="bodyS"
                weight="regular"
                className={styles.supportDataText}
              >
                {t("investment_page.eh40")}
              </Typography>
              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    Ein Effizienzhaus 40 (EH40) ist ein reiner energetischer
                    Standard, der durch das Gebäudeenergiegesetz (GEG) definiert
                    wird. Ein Gebäude dieser Klasse benötigt nur 40 Prozent der
                    Primärenergie, die ein vergleichbares Referenzgebäude laut
                    gesetzlicher Vorgabe verbrauchen darf. Es ist also um 60
                    Prozent energieeffizienter. Der Nachweis, dass diese
                    strengen energetischen Anforderungen eingehalten werden,
                    muss durch eine Energiebedarfsberechnung erfolgen und wird
                    von einem zertifizierten, unabhängigen Energieexperten
                    bestätigt.
                  </Typography>
                </div>
              </Popover>
            </div>
          ))}
        {conclusionsPro?.baseCalculation?.isKfw297Limit150Enabled && (
          <div className={styles.supportDataItem}>
            <CheckIcon className={styles.checkIcon} />
            <Typography
              variant="bodyS"
              weight="regular"
              className={styles.supportDataText}
            >
              {t("investment_page.qng")}
            </Typography>
            <Popover
              placement="top"
              triggerType="hover"
              yOffset={15}
              floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
              triggerClassName={styles.trigger}
              triggerElement={() => (
                <InfoIcon className={styles.chartListIcon} />
              )}
            >
              <div className={styles.chartListPopover}>
                <Typography variant="bodyS" weight="medium">
                  Das EH40 ist eine notwendige, aber nicht ausreichende
                  Voraussetzung, um das QNG-Siegel zu erhalten. Das QNG-Siegel
                  ist ein staatliches Gütesiegel, das die ganzheitliche
                  Nachhaltigkeit eines Gebäudes bewertet. Es geht über die reine
                  Energieeffizienz hinaus und prüft zusätzliche Kriterien wie
                  Verwendung von Öko-Baumaterialien und die Einhaltung strenger
                  Grenzwerte für Treibhausgasemissionen über den gesamten
                  Lebenszyklus und Berücksichtigung der Nutzerbedürfnisse, wie
                  Behindertengerechtigkeit, Komfort und eine gesunde Raumluft.
                  Auch die Bewertung der langfristigen Flexibilität und
                  Anpassungsfähigkeit des Gebäudes an künftige
                  Nutzungsanforderungen gehört dazu, was den Wert langfristig
                  sichert.
                </Typography>
              </div>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectDetails;
